import { Box3, Group, Object3D, OrthographicCamera, Scene, Vector3, WebGLRenderer } from 'three';

import { renderActiveScene, SkyView } from '../../viewer/skyview';
import { SunManager } from '../../viewer/sun-manager';

/**
 * Gets the callback for rendering SkyView
 */
function getRenderSkyviewCallback(options?: { clearColor?: number; hideAssets?: boolean }) {
  return function (skyview: SkyView) {
    renderActiveScene(
      skyview.renderer,
      skyview.css2dRenderer,
      skyview.css3dRenderer,
      skyview.baseScene,
      {
        occlusionCulling: skyview.occlusionCulling,
        clearColor: options?.clearColor,
        hideAssets: options?.hideAssets,
      },
    );
  };
}

/**
 * Creates a screenshot based on the current view using the active scene and the active camera.
 */
export function redrawSkyViewWithOptions(
  skyview: SkyView,
  options?: { clearColor?: number; hideAssets?: boolean },
): void {
  // Wrap the render callback with pre processing and post processing steps.
  const renderCallback = getRenderSkyviewCallback(options);

  renderCallback(skyview);
}

/**
 * Creates a screenshot of a three object.
 */
export function createScreenshotOfThreeObject(
  originalObject: Object3D | Group,
  imageSizePixels: number,
) {
  // Clone the object, otherwise the object will disappear from the original scene when adding it to the screenshot scene one.
  const object = originalObject.clone(true);
  object.updateMatrixWorld(true);
  object.traverse((x) => {
    x.visible = true;
  });

  const screenShotScene = new Scene();
  screenShotScene.add(object);

  for (const light of SunManager.instance.cloneLights()) {
    screenShotScene.add(light);
  }

  const camera = getOrthographicCameraForObject(new Box3().setFromObject(object));

  const renderer = rendererHelpers.getWebGLRenderer();
  renderer.setSize(imageSizePixels, imageSizePixels);
  renderer.setClearColor(0xffffff, 1);
  renderer.render(screenShotScene, camera);

  return renderer.domElement.toDataURL('image/png');
}

/**
 * Configures an orthograpihc camera that is looking at any three object.
 * The camera will cover the whole bounding box of the object and always have
 * an aspect ration of 1:1
 */
function getOrthographicCameraForObject(boundingBox: Box3) {
  const center = boundingBox.getCenter(new Vector3());
  const size = boundingBox.getSize(new Vector3());
  const maxDimension = Math.max(size.x, size.y, size.z);
  const aspectRatio = 1;

  // Adjust the camera parameters so the bounding box fits into the view.
  const left = (aspectRatio * maxDimension) / -2;
  const right = (aspectRatio * maxDimension) / 2;
  const top = maxDimension / 2;
  const bottom = maxDimension / -2;
  const near = 0.1;
  const far = size.z * 2 + 20;

  const camera = new OrthographicCamera(left, right, top, bottom, near, far);

  // Position the camera on the top of the bounding box with some distance.
  camera.position.set(center.x, center.y, boundingBox.max.z + 10);
  camera.lookAt(center);
  camera.updateProjectionMatrix();

  return camera;
}

/**
 * Work-around in order to be able to mock the module in the unit tests.
 */
export const rendererHelpers = {
  getWebGLRenderer: () => {
    return new WebGLRenderer({ antialias: false });
  },
};
